import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ToastrService } from '@shared/services/toastr.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AdminService } from '@shared/services/admin.service';
import { AuthService } from '@shared/services/auth.service';
import { AdminOrganizationService, OrganizationListItem } from '@shared/services/admin-organization.service';
import { ADD_ROUTE } from 'app/app-routing.constants';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormGroupType } from '@shared/utils/formgroup-type';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { TimestampCellComponent } from '@shared/components/timestamp-cell/timestamp-cell.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { ModalBasicComponent } from '@shared/components/modal-basic/modal-basic.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    BadgeOrganizationComponent,
    TimestampCellComponent,
    NgxPaginationModule,
    ModalBasicComponent,
    TranslatePipe,
  ],
})
export class OrganizationsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('remove') remove: any;

  protected readonly ADD_ROUTE = ADD_ROUTE;

  private destroy$ = new Subject<void>();

  organization: OrganizationListItem;
  organizations: OrganizationListItem[] = [];
  clusters: any = [];

  page: number = 1;
  pageSize: number = 20;
  totalItems: number = 0;
  organizationFormGroup: FormGroupType<{ search: string; ownsSite: boolean }>;
  constructor(
    private toastr: ToastrService,
    public auth: AuthService,
    private admin: AdminService,
    private adminOrganizationService: AdminOrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.organizationFormGroup = this.fb.group({ search: new FormControl(''), ownsSite: new FormControl(false) });

    this.route.queryParams.subscribe((queryParams) => {
      this.organizationFormGroup.get('search').setValue(queryParams.search, { emitEvent: false });
      this.organizationFormGroup.get('ownsSite').setValue(queryParams.ownsSite, { emitEvent: false });
      this.refreshList();
    });
  }

  ngAfterViewInit(): void {
    this.organizationFormGroup.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.updateQueryParams(this.organizationFormGroup.getRawValue() as Record<string, any>);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateQueryParams(params: Record<string, any>) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  connectAsOwner(organization: OrganizationListItem) {
    this.admin.connectAsUser(organization.owner, organization.code);
  }

  modalRemove(p: OrganizationListItem) {
    this.remove.show();
    this.organization = { ...p };
  }

  changePage(evt: number) {
    this.page = evt;
    this.refreshList();
  }

  async refreshList() {
    const searchTerm = this.organizationFormGroup.get('search').value;
    const ownsSite = this.organizationFormGroup.get('ownsSite').value;
    let res: any = null;
    try {
      res = await this.adminOrganizationService.getOrganizations({ searchTerm, ownsSite }, this.page - 1);
    } catch (error) {
      return this.toastr.error(error.error);
    }

    this.organizations = res?.content;
    this.totalItems = res?.totalElements;
  }

  async removeOrganization(id) {
    this.adminOrganizationService.deleteOrganization(id).subscribe(() => {
      this.toastr.success();
      this.remove.hide();
      this.refreshList();
    });
  }
}
